// ============================================================
// Chess Arena — Main Menu Tutorial
// ============================================================
// HTML-based tutorial overlay for the main menu screen.
// Guides first-time players through the chest unlock flow.
// ============================================================

interface TutorialStep {
  stepId: string;
  spotlightTarget: string | null;
  text: string;
  requiredAction: string | null;
}

export interface MainMenuTutorialCallbacks {
  onChestTapLocked: () => void;    // player taps locked chest
  onChestTapReady: () => void;     // player taps ready chest
  onAutoAccelerate: () => void;    // auto-skip the chest timer
  onComplete: () => void;          // tutorial finished
}

export class MainMenuTutorial {
  private overlay: HTMLElement | null = null;
  private steps: TutorialStep[] = [];
  private currentStepIndex = -1;
  private callbacks: MainMenuTutorialCallbacks | null = null;
  private parentContainer: HTMLElement | null = null;
  private active = false;

  start(
    parentContainer: HTMLElement,
    steps: TutorialStep[],
    callbacks: MainMenuTutorialCallbacks,
  ): void {
    this.parentContainer = parentContainer;
    this.steps = steps;
    this.callbacks = callbacks;
    this.currentStepIndex = -1;
    this.active = true;
    this.advanceStep();
  }

  isActive(): boolean {
    return this.active;
  }

  /** Called externally when the UI has updated and the tutorial should advance. */
  notifyAction(action: string): void {
    if (!this.active) return;
    const step = this.steps[this.currentStepIndex];
    if (step?.requiredAction === action) {
      this.advanceStep();
    }
  }

  /** Rebuild the current step's overlay (e.g. after chest UI re-renders). */
  refreshCurrentStep(): void {
    if (!this.active || this.currentStepIndex < 0) return;
    this.removeOverlay();
    this.showStep(this.steps[this.currentStepIndex]);
  }

  private advanceStep(): void {
    this.removeOverlay();
    this.currentStepIndex++;

    if (this.currentStepIndex >= this.steps.length) {
      this.complete();
      return;
    }

    this.showStep(this.steps[this.currentStepIndex]);
  }

  private showStep(step: TutorialStep): void {
    if (!this.parentContainer) return;

    this.overlay = document.createElement('div');
    this.overlay.className = 'mm-tutorial-overlay';

    let targetEl: HTMLElement | null = null;
    if (step.spotlightTarget) {
      targetEl = this.resolveTarget(step.spotlightTarget);
    }

    if (targetEl) {
      this.buildSpotlightOverlay(targetEl);
    }

    // Text bubble
    const bubble = document.createElement('div');
    bubble.className = 'mm-tutorial-bubble';

    const mascot = document.createElement('img');
    mascot.className = 'mm-tutorial-mascot';
    mascot.src = '/assets/tutorial/mascot_king.png';
    mascot.alt = '';
    bubble.appendChild(mascot);

    const textEl = document.createElement('div');
    textEl.className = 'mm-tutorial-text';
    textEl.textContent = step.text;
    bubble.appendChild(textEl);

    // Position bubble relative to target
    if (targetEl) {
      const rect = targetEl.getBoundingClientRect();
      const parentRect = this.parentContainer.getBoundingClientRect();
      const targetCenterY = rect.top - parentRect.top + rect.height / 2;

      if (targetCenterY > parentRect.height / 2) {
        bubble.classList.add('mm-tutorial-bubble-top');
      } else {
        bubble.classList.add('mm-tutorial-bubble-bottom');
      }
    } else {
      bubble.classList.add('mm-tutorial-bubble-center');
    }

    if (!step.requiredAction) {
      const hint = document.createElement('div');
      hint.className = 'mm-tutorial-hint';
      hint.textContent = 'Tap to continue';
      bubble.appendChild(hint);
    }

    this.overlay.appendChild(bubble);

    // Wire interaction based on the required action
    switch (step.requiredAction) {
      case 'chest_tap_locked':
        if (targetEl) {
          const zone = this.createClickZone(targetEl);
          zone.addEventListener('click', (e) => {
            e.stopPropagation();
            this.callbacks?.onChestTapLocked();
          });
          this.overlay.appendChild(zone);
        }
        break;

      case 'chest_tap_ready':
        if (targetEl) {
          const zone = this.createClickZone(targetEl);
          zone.addEventListener('click', (e) => {
            e.stopPropagation();
            this.callbacks?.onChestTapReady();
          });
          this.overlay.appendChild(zone);
        }
        break;

      case 'auto_accelerate':
        // Auto-fire the accelerate callback after a short pause
        // so the player sees the timer briefly before it skips
        setTimeout(() => {
          if (this.active) {
            this.callbacks?.onAutoAccelerate();
          }
        }, 1500);
        break;

      default:
        // Tap anywhere to continue
        this.overlay.addEventListener('click', () => {
          this.advanceStep();
        });
        break;
    }

    this.parentContainer.appendChild(this.overlay);
  }

  private buildSpotlightOverlay(targetEl: HTMLElement): void {
    if (!this.overlay || !this.parentContainer) return;

    const rect = targetEl.getBoundingClientRect();
    const parentRect = this.parentContainer.getBoundingClientRect();
    const padding = 10;

    const sx = rect.left - parentRect.left - padding;
    const sy = rect.top - parentRect.top - padding;
    const sw = rect.width + padding * 2;
    const sh = rect.height + padding * 2;

    this.overlay.style.background = 'none';

    const darkColor = 'rgba(0, 0, 0, 0.75)';
    const ph = parentRect.height;
    const pw = parentRect.width;

    this.addStrip(0, 0, pw, sy, darkColor);
    this.addStrip(0, sy + sh, pw, ph - (sy + sh), darkColor);
    this.addStrip(0, sy, sx, sh, darkColor);
    this.addStrip(sx + sw, sy, pw - (sx + sw), sh, darkColor);

    const border = document.createElement('div');
    border.className = 'mm-tutorial-spotlight-border';
    border.style.left = `${sx}px`;
    border.style.top = `${sy}px`;
    border.style.width = `${sw}px`;
    border.style.height = `${sh}px`;
    this.overlay.appendChild(border);
  }

  private addStrip(x: number, y: number, w: number, h: number, color: string): void {
    if (w <= 0 || h <= 0) return;
    const strip = document.createElement('div');
    strip.style.position = 'absolute';
    strip.style.left = `${x}px`;
    strip.style.top = `${y}px`;
    strip.style.width = `${w}px`;
    strip.style.height = `${h}px`;
    strip.style.background = color;
    this.overlay!.appendChild(strip);
  }

  private createClickZone(targetEl: HTMLElement): HTMLElement {
    const rect = targetEl.getBoundingClientRect();
    const parentRect = this.parentContainer!.getBoundingClientRect();
    const padding = 10;

    const zone = document.createElement('div');
    zone.className = 'mm-tutorial-click-zone';
    zone.style.left = `${rect.left - parentRect.left - padding}px`;
    zone.style.top = `${rect.top - parentRect.top - padding}px`;
    zone.style.width = `${rect.width + padding * 2}px`;
    zone.style.height = `${rect.height + padding * 2}px`;
    return zone;
  }

  private resolveTarget(target: string): HTMLElement | null {
    if (!this.parentContainer) return null;

    switch (target) {
      case 'chestLocked':
        return this.parentContainer.querySelector('.chest-slot-locked') as HTMLElement | null;
      case 'chestUnlocking':
        return this.parentContainer.querySelector('.chest-slot-unlocking') as HTMLElement | null;
      case 'chestReady':
        return this.parentContainer.querySelector('.chest-slot-ready') as HTMLElement | null;
      case 'chestTray':
        return this.parentContainer.querySelector('.mm-chest-tray') as HTMLElement | null;
      case 'playButton':
        return this.parentContainer.querySelector('[data-ref="levelBtn"]') as HTMLElement | null;
      case 'tasksButton':
        return this.parentContainer.querySelector('[data-ref="tasksBtn"]') as HTMLElement | null;
      default:
        return this.parentContainer.querySelector(target) as HTMLElement | null;
    }
  }

  private removeOverlay(): void {
    if (this.overlay) {
      this.overlay.remove();
      this.overlay = null;
    }
  }

  private complete(): void {
    this.removeOverlay();
    this.active = false;
    this.callbacks?.onComplete();
  }

  destroy(): void {
    this.removeOverlay();
    this.active = false;
  }
}
